import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

const CONV_STORAGE_KEY = "siem_genie_conv_v1"

export default class extends Controller {
  static targets = ["panel", "trigger", "geniePanel", "chatHistory", "chatInput"]
  static values = { url: String, open: Boolean }

  #timelineChart = null
  #conversation = [] // [{role: "user"|"assistant", content: string}]

  connect() {
    this.openValue = false
    try {
      this.#conversation = JSON.parse(sessionStorage.getItem(CONV_STORAGE_KEY) || "[]")
    } catch { this.#conversation = [] }
  }

  disconnect() {
    this.#timelineChart?.destroy()
  }

  toggle() {
    this.openValue = !this.openValue
    if (this.openValue) {
      this.#conversation = []
      sessionStorage.removeItem(CONV_STORAGE_KEY)
      this.#analyze()
    }
  }

  async sendFollowup(event) {
    if (event.type === "keypress" && event.key !== "Enter") return
    if (!this.hasChatInputTarget) return
    const question = this.chatInputTarget.value.trim()
    if (!question) return
    this.chatInputTarget.value = ""

    this.#appendChatBubble("user", question)
    this.#conversation.push({ role: "user", content: question })
    this.#saveConversation()

    const thinking = this.#appendChatBubble("assistant", "…")

    const formData = new FormData()
    const searchParams = new URLSearchParams(window.location.search)
    for (const [key, val] of searchParams) formData.append(key, val)
    formData.append("conversation_json", JSON.stringify(this.#conversation))
    formData.append("authenticity_token", document.querySelector('meta[name="csrf-token"]')?.content || "")

    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: { Accept: "text/html, application/xhtml+xml", "Turbo-Frame": "siem-genie-panel" },
      })
      const html = await resp.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const summary = doc.querySelector(".siem-genie-summary")?.textContent?.trim() || "No answer."
      thinking.textContent = summary
      this.#conversation.push({ role: "assistant", content: summary })
      this.#saveConversation()
    } catch {
      thinking.textContent = "Request failed. Please try again."
    }
  }

  openValueChanged() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.toggle("open", this.openValue)
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.toggle("active", this.openValue)
    }
  }

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    const panel = this.element.querySelector(".siem-genie-panel")
    if (!panel) return

    panel.querySelectorAll(".siem-genie-tab").forEach(btn => {
      btn.classList.toggle("active", btn.dataset.tab === tab)
    })
    panel.querySelectorAll(".siem-genie-tab-pane").forEach(pane => {
      pane.classList.toggle("hidden", pane.dataset.tabPane !== tab)
    })

    if (tab === "timeline") this.#renderTimeline(panel)
    if (tab === "graph") this.#renderEntityGraph(panel)
  }

  applyQuery({ params: { query } }) {
    const textarea = document.querySelector("textarea[name='query']")
    if (textarea) {
      textarea.value = query
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      const form = textarea.closest("form")
      if (form) form.requestSubmit()
    }
  }

  openQueryInNewTab({ params: { query } }) {
    window.dispatchEvent(new CustomEvent("siem:open-tab", { detail: { query } }))
  }

  expandInViz() {
    window.dispatchEvent(new CustomEvent("siem:switch-mode", { detail: { mode: "viz" } }))
  }

  #analyze() {
    const frame = this.element.querySelector("turbo-frame#siem-genie-panel")
    if (!frame) return

    const formData = new FormData()
    const searchParams = new URLSearchParams(window.location.search)
    for (const [key, val] of searchParams) {
      formData.append(key, val)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrfToken) formData.append("authenticity_token", csrfToken)

    this.#timelineChart?.destroy()
    this.#timelineChart = null

    frame.innerHTML = `
      <div class="siem-genie-panel">
        <div class="siem-genie-header"><i class="fa fa-magic"></i> Pythia</div>
        <div class="siem-genie-loading">
          <i class="fa fa-spin fa-spinner"></i>
          Analyzing results...
        </div>
      </div>`

    fetch(this.urlValue, {
      method: "POST",
      body: formData,
      headers: {
        "Accept": "text/html, application/xhtml+xml",
        "Turbo-Frame": "siem-genie-panel",
      },
    })
      .then(r => r.text())
      .then(html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const content = doc.querySelector("turbo-frame#siem-genie-panel")
        if (content) frame.innerHTML = content.innerHTML
      })
      .catch(() => {
        frame.innerHTML = `
          <div class="siem-genie-panel">
            <div class="siem-genie-header"><i class="fa fa-magic"></i> Pythia</div>
            <div class="siem-genie-error">Analysis failed. Please try again.</div>
          </div>`
      })
  }

  #renderTimeline(panel) {
    this.#timelineChart?.destroy()
    this.#timelineChart = null

    const el = panel.querySelector("#siem-timeline-chart")
    if (!el || el.dataset.rendered) return

    const events = JSON.parse(panel.dataset.timelineEvents || "[]")
    if (!events.length) {
      el.innerHTML = '<p class="siem-genie-empty"><i class="fa fa-info-circle"></i> No timeline events extracted.</p>'
      return
    }

    const entities = [...new Set(events.map(e => e.entity))]
    const severityColor = { critical: "#d9534f", high: "#e67e22", medium: "#f0ad4e", info: "#217DBB" }

    entities.map((entity, idx) => ({
      name: entity,
      data: events
        .filter(e => e.entity === entity)
        .map(e => ({
          x: entity,
          y: new Date(e.timestamp_iso).getTime(),
          meta: e,
        })),
    }))

    const flatData = events.map(e => ({
      x: e.entity,
      y: new Date(e.timestamp_iso).getTime(),
      fillColor: severityColor[e.severity] || severityColor.info,
      meta: e,
    }))

    const options = {
      chart: {
        type: "scatter",
        height: Math.max(120, entities.length * 40 + 40),
        toolbar: { show: false },
        animations: { enabled: false },
      },
      series: [{ name: "Events", data: flatData }],
      xaxis: { categories: entities, type: "category", labels: { style: { fontSize: "10px" } } },
      yaxis: { type: "datetime", labels: { format: "HH:mm:ss", style: { fontSize: "10px" } } },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const e = flatData[dataPointIndex]?.meta
          if (!e) return ""
          return `<div class="siem-timeline-tooltip">
            <strong>${this.#esc(e.event_type)}</strong><br>
            ${this.#esc(e.entity)}<br>
            <span class="siem-badge-${e.severity}">${e.severity}</span>
          </div>`
        },
      },
      markers: { size: 7 },
      dataLabels: { enabled: false },
      grid: { padding: { left: 10, right: 10 } },
    }

    el.dataset.rendered = "1"
    this.#timelineChart = new ApexCharts(el, options)
    this.#timelineChart.render()
  }

  #renderEntityGraph(panel) {
    const el = panel.querySelector("#siem-entity-graph")
    if (!el || el.dataset.rendered) return

    const riskEntities = JSON.parse(panel.dataset.riskEntities || "[]")
    const edges = JSON.parse(panel.dataset.entityEdges || "[]")

    const nodeSet = new Set(riskEntities.map(e => e.entity))
    edges.forEach(e => { nodeSet.add(e.source); nodeSet.add(e.target) })
    const nodes = [...nodeSet]

    if (nodes.length === 0) {
      el.innerHTML = '<p class="siem-genie-empty"><i class="fa fa-info-circle"></i> No entity relationships found.</p>'
      return
    }

    el.dataset.rendered = "1"
    el.innerHTML = this.#buildGraphSVG(nodes, edges, riskEntities, el.clientWidth || 280)
  }

  #buildGraphSVG(nodes, edges, riskEntities, width) {
    const H = Math.max(200, nodes.length * 35 + 60)
    const cx = width / 2, cy = H / 2
    const riskSet = new Set(riskEntities.map(e => e.entity))

    // Spring layout: start circular, run iterations
    const pos = nodes.map((_, i) => {
      const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2
      const R = Math.min(cx, cy) * 0.7
      return { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle), vx: 0, vy: 0 }
    })

    const nodeIndex = Object.fromEntries(nodes.map((n, i) => [n, i]))
    const REPEL = 1800, ATTRACT = 0.04, DAMP = 0.8, ITER = 80

    for (let t = 0; t < ITER; t++) {
      const fx = new Array(nodes.length).fill(0)
      const fy = new Array(nodes.length).fill(0)

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = pos[i].x - pos[j].x, dy = pos[i].y - pos[j].y
          const d2 = Math.max(dx * dx + dy * dy, 1)
          const f = REPEL / d2
          fx[i] += f * dx; fy[i] += f * dy
          fx[j] -= f * dx; fy[j] -= f * dy
        }
      }

      edges.forEach(e => {
        const si = nodeIndex[e.source], ti = nodeIndex[e.target]
        if (si === undefined || ti === undefined) return
        const dx = pos[ti].x - pos[si].x, dy = pos[ti].y - pos[si].y
        fx[si] += ATTRACT * dx; fy[si] += ATTRACT * dy
        fx[ti] -= ATTRACT * dx; fy[ti] -= ATTRACT * dy
      })

      for (let i = 0; i < nodes.length; i++) {
        pos[i].vx = (pos[i].vx + fx[i]) * DAMP
        pos[i].vy = (pos[i].vy + fy[i]) * DAMP
        pos[i].x = Math.max(30, Math.min(width - 30, pos[i].x + pos[i].vx))
        pos[i].y = Math.max(20, Math.min(H - 20, pos[i].y + pos[i].vy))
      }
    }

    let svg = `<svg viewBox="0 0 ${width} ${H}" xmlns="http://www.w3.org/2000/svg" class="siem-graph-svg">`
    svg += `<defs><marker id="arr" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
      <path d="M0,0 L0,6 L6,3 z" class="siem-graph-arrow"/>
    </marker></defs>`

    edges.forEach(e => {
      const si = nodeIndex[e.source], ti = nodeIndex[e.target]
      if (si === undefined || ti === undefined) return
      const s = pos[si], t = pos[ti]
      const mx = (s.x + t.x) / 2, my = (s.y + t.y) / 2
      svg += `<line x1="${s.x.toFixed(1)}" y1="${s.y.toFixed(1)}" x2="${t.x.toFixed(1)}" y2="${t.y.toFixed(1)}" class="siem-graph-edge" marker-end="url(#arr)"/>`
      svg += `<text x="${mx.toFixed(1)}" y="${(my - 3).toFixed(1)}" class="siem-graph-edge-label">${this.#esc(e.label)}</text>`
    })

    nodes.forEach((n, i) => {
      const p = pos[i]
      const isRisk = riskSet.has(n)
      svg += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="9" class="siem-graph-node${isRisk ? " siem-graph-node-risk" : ""}"/>`
      svg += `<text x="${p.x.toFixed(1)}" y="${(p.y + 20).toFixed(1)}" text-anchor="middle" class="siem-graph-node-label">${this.#esc(this.#truncate(n, 18))}</text>`
    })

    return svg + "</svg>"
  }

  #truncate(str, max) {
    return str.length > max ? str.slice(0, max - 1) + "…" : str
  }

  #appendChatBubble(role, text) {
    if (!this.hasChatHistoryTarget) return { textContent: "" }
    const div = document.createElement("div")
    div.className = `siem-chat-bubble siem-chat-${role}`
    div.textContent = text
    this.chatHistoryTarget.appendChild(div)
    this.chatHistoryTarget.scrollTop = this.chatHistoryTarget.scrollHeight
    return div
  }

  #saveConversation() {
    try { sessionStorage.setItem(CONV_STORAGE_KEY, JSON.stringify(this.#conversation)) } catch { /* ignore */ }
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
